// `/admin/international-withdrawals/$id` (WEB-135): CONTEÚDO do `RouteDrawer` da rota-pai.
// O cabeçalho (título, id e fechar) é do drawer, e a fila continua desenhada embaixo.
//
// Assumir, marcar como pago e rejeitar exigem step-up: a API devolve `403 mfa_required` e
// o `StepUpDialog` de `api/client.ts` pede a confirmação e reexecuta (`spec/17` §4.3).
// O `GET` do detalhe não exige: o endereço completo vem sempre.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { adminMeQueryOptions, adminsQueryOptions } from '../api/queries/admin-system.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { IntlWithdrawalDetailView } from '../features/admin-international-withdrawals/detail.tsx';
import { isNotFound } from '../features/admin-international-withdrawals/errors.ts';
import {
  claimIntlWithdrawal,
  INTL_ACTION_INVALIDATES,
  intlDetailQueryOptions,
  type MarkPaidBody,
  markIntlWithdrawalPaid,
  type RejectBody,
  rejectIntlWithdrawal,
} from '../features/admin-international-withdrawals/queries.ts';
import { adminNameMap } from '../features/admin-international-withdrawals/rules.ts';

function AdminInternationalWithdrawalPage() {
  const { id } = Route.useParams();
  // O `search` é da rota-PAI (a aba e o recorte por lojista): trocar de pedido dentro do
  // drawer não pode apagar o filtro da fila que ficou embaixo.
  const search = useSearch({ from: '/admin/international-withdrawals' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detail = useQuery(intlDetailQueryOptions(id));
  const me = useQuery(adminMeQueryOptions());
  const admins = useQuery(adminsQueryOptions());

  /**
   * Toda ação termina igual, deu certo ou não: recarrega a fila e o detalhe. Um
   * `invalid_state` precisa aparecer com o estado já atualizado, senão a tela mostra um
   * erro ao lado de um status velho. O `await` segura o `mutateAsync` até o detalhe novo
   * chegar: o diálogo só fecha com o pedido já no estado certo embaixo dele.
   */
  const refresh = async (): Promise<void> => {
    await Promise.all(
      INTL_ACTION_INVALIDATES.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };

  const claim = useMutation({ mutationFn: () => claimIntlWithdrawal(id), onSettled: refresh });
  const markPaid = useMutation({
    mutationFn: (body: MarkPaidBody) => markIntlWithdrawalPaid(id, body),
    onSuccess: () => {
      toast.success('Saque marcado como pago', {
        description: 'O lojista recebe o e-mail com o txid.',
      });
    },
    onSettled: refresh,
  });
  const reject = useMutation({
    mutationFn: (body: RejectBody) => rejectIntlWithdrawal(id, body),
    onSuccess: () => {
      toast.success('Saque rejeitado', { description: 'O saldo voltou para o lojista.' });
    },
    onSettled: refresh,
  });

  if (detail.isPending) {
    return <CardSkeleton lines={6} />;
  }

  // 404 pelo `code`, nunca pelo texto do status (`spec/17` §4.3 item 1).
  if (isNotFound(detail.error)) {
    return (
      <Empty
        icon="wallet"
        title="Saque não encontrado"
        hint="Este pedido não existe ou é de teste: só pedidos reais aparecem aqui. Confira o id na fila."
      />
    );
  }

  // Erro só quando não há dado: com o polling de 30 s, um flake no refetch de fundo
  // trocaria o endereço e o valor na tela por uma mensagem de falha no meio do pagamento.
  if (detail.data === undefined) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }

  return (
    <IntlWithdrawalDetailView
      // `key`: trocar de pedido dentro do drawer zera diálogo aberto e texto digitado.
      key={id}
      detail={detail.data}
      meId={me.data?.id}
      adminNames={adminNameMap(admins.data)}
      onClaim={() => claim.mutateAsync()}
      onMarkPaid={(body) => markPaid.mutateAsync(body)}
      onReject={(body) => reject.mutateAsync(body)}
      onSelectPrevious={(withdrawal) => {
        void navigate({
          to: '/admin/international-withdrawals/$id',
          params: { id: withdrawal.id },
          search,
        });
      }}
      onSelectSeller={(sellerId) => {
        void navigate({
          to: '/admin/international-withdrawals',
          search: { status: search.status, seller_id: sellerId },
        });
      }}
    />
  );
}

export const Route = createFileRoute('/admin/international-withdrawals/$id')({
  component: AdminInternationalWithdrawalPage,
});
